import type { CustomerDashboardAccount } from "@ternary/contracts"
import { useTranslation } from "react-i18next"

function SidebarIcon({ name }: { name: "account" | "external" | "sign-out" }) {
  if (name === "sign-out") {
    // A door with an arrow leaving it, so Sign Out does not share the external links' icon.
    return (
      <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5" />
        <path d="M21 12H9" />
      </svg>
    )
  }
  if (name === "account") {
    return (
      <svg aria-hidden="true" viewBox="0 0 23 23">
        <path
          clipRule="evenodd"
          d="M12.771 0a2 2 0 0 1 1.848 1.236l.056.152.555 1.73.988-.74a2 2 0 0 1 2.484.07l.128.118 1.604 1.604a2 2 0 0 1 .285 2.468l-.098.144-.738.987 1.729.556a2 2 0 0 1 1.382 1.742l.006.162v2.542a2 2 0 0 1-1.236 1.848l-.152.056-1.73.555.74.988a2 2 0 0 1-.07 2.484l-.118.128-1.604 1.604a2 2 0 0 1-2.468.285l-.144-.098-.987-.738-.556 1.729a2 2 0 0 1-1.742 1.382L12.771 23h-2.542a2 2 0 0 1-1.848-1.236l-.056-.152-.555-1.73-.988.74a2 2 0 0 1-2.484-.07l-.128-.118-1.604-1.604a2 2 0 0 1-.285-2.468l.098-.144.738-.987-1.729-.556a2 2 0 0 1-1.382-1.742L0 12.771v-2.542A2 2 0 0 1 1.236 8.38l.152-.056 1.73-.555-.74-.988a2 2 0 0 1 .07-2.484l.118-.128L4.17 2.566a2 2 0 0 1 2.468-.285l.144.098.987.738.556-1.729A2 2 0 0 1 10.067.006L10.229 0h2.542Zm0 2h-2.542l-.555 1.729a2 2 0 0 1-.303.586 2 2 0 0 1-2.664.496l-.135-.092-.988-.738-1.603 1.603.738.988a2 2 0 0 1 .302.586 2 2 0 0 1-1.135 2.462l-.147.054L2 10.229v2.542l1.729.555a2 2 0 0 1 .586.303 2 2 0 0 1 .496 2.664l-.092.135-.738.988 1.603 1.603.988-.738a2 2 0 0 1 .586-.302 2 2 0 0 1 2.462 1.135l.054.147L10.229 21h2.542l.555-1.729a2 2 0 0 1 .303-.586 2 2 0 0 1 2.664-.496l.135.092.988.738 1.603-1.603-.738-.988a2 2 0 0 1-.302-.586 2 2 0 0 1 1.135-2.462l.147-.054L21 12.771v-2.542l-1.729-.555a2 2 0 0 1-.586-.303 2 2 0 0 1-.496-2.664l.092-.135.738-.988-1.603-1.603-.988.738a2 2 0 0 1-.586.302 2 2 0 0 1-2.462-1.135l-.054-.147L12.771 2Zm1.557 6.672a4 4 0 1 1-5.656 5.656 4 4 0 0 1 5.656-5.656Zm-4.242 1.414a2 2 0 1 0 2.828 2.828 2 2 0 0 0-2.828-2.828Z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </svg>
    )
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 24 22">
      <path
        clipRule="evenodd"
        d="M18.6 0A3.4 3.4 0 0 1 22 3.4V8a1 1 0 1 1-2 0V3.4A1.4 1.4 0 0 0 18.6 2H3.4A1.4 1.4 0 0 0 2 3.4v15.2A1.4 1.4 0 0 0 3.4 20H8a1 1 0 1 1 0 2H3.4A3.4 3.4 0 0 1 0 18.6V3.4A3.4 3.4 0 0 1 3.4 0h15.2Z"
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="m10.53 19.652.043.116a1 1 0 0 0 1.62.298l1.771-1.771 2.788 2.788a1 1 0 0 0 1.414 0l2.829-2.829.083-.094a1 1 0 0 0-.083-1.32l-2.788-2.788 1.707-1.706a1 1 0 0 0-.414-1.664L8.37 7.273a1 1 0 0 0-1.249 1.249l3.409 11.13Zm-.946-9.916 7.74 2.371-1.238 1.238-.083.095a1 1 0 0 0 .083 1.32l2.787 2.787-1.414 1.414-2.787-2.787-.094-.083a1 1 0 0 0-1.32.083l-1.303 1.302-2.371-7.74Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  )
}

export function LegacyAccountSidebar({
  data,
  menuOpen,
  onToggle,
}: {
  data: CustomerDashboardAccount
  menuOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      {/* The sidebar's head from 992px; below it, the 56px top bar with the menu button. */}
      <header className="legacy-dashboard-topbar">
        <button
          className="legacy-dashboard-menu"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="customer-account-sidebar"
          onClick={onToggle}
        >
          <span aria-hidden="true">☰</span>
          <span className="sr-only">{t("Toggle account menu")}</span>
        </button>
        <div className="legacy-dashboard-brand">
          {data.organization.logoUrl ? <img src={data.organization.logoUrl} alt="" /> : null}
          <strong>{data.organization.name}</strong>
        </div>
      </header>
      {menuOpen ? (
        <button
          className="legacy-dashboard-scrim"
          type="button"
          aria-label={t("Close account menu")}
          onClick={onToggle}
        />
      ) : null}
      <aside
        className={`legacy-dashboard-sidebar ${menuOpen ? "is-open" : ""}`}
        id="customer-account-sidebar"
      >
        <nav aria-label={t("Customer account")}>
          <a className="active" href="/dashboard" onClick={onToggle}>
            <span>
              <SidebarIcon name="account" />
            </span>
            {t("Account")}
          </a>
          {data.organization.sidebar.map((item) => (
            <a href={item.url} key={`${item.name}-${item.url}`} onClick={onToggle}>
              <span>
                <SidebarIcon name="external" />
              </span>
              {item.name}
            </a>
          ))}
        </nav>
        <a className="legacy-dashboard-logout" href="/logout">
          <span>
            <SidebarIcon name="sign-out" />
          </span>
          {t("Sign Out")}
        </a>
      </aside>
    </>
  )
}
